import { CheckRounded, ErrorOutlineRounded, InboxOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { designTokens } from '../tokens';

export type ResultStateTone = 'neutral' | 'success' | 'warning' | 'danger';

export type ResultStateProps = {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  tone?: ResultStateTone;
  /** 부모 표면 안에서는 별도의 배경과 외곽선을 그리지 않습니다. */
  embedded?: boolean;
  /** 카드·팝업 안의 짧은 안내는 여백과 아이콘을 줄이고 자연 높이를 사용합니다. */
  density?: 'standard' | 'compact';
};

const tonePresentation = {
  neutral: { Icon: InboxOutlined, color: designTokens.color.neutralText, background: designTokens.color.neutralRaisedSurface },
  success: { Icon: CheckRounded, color: designTokens.color.successText, background: designTokens.color.successSoft },
  warning: { Icon: WarningAmberRounded, color: designTokens.color.warningDark, background: designTokens.color.warningSoft },
  danger: { Icon: ErrorOutlineRounded, color: designTokens.color.dangerText, background: designTokens.color.dangerSoft },
} as const;

/** 결과의 의미와 다음 동작은 호출자가 소유하고, 제목·설명·액션의 배치와 알림 역할을 통일합니다. */
export function ResultState({ title, description, action, tone = 'neutral', embedded = false, density = 'standard' }: ResultStateProps) {
  const { Icon, color, background } = tonePresentation[tone];
  const compact = density === 'compact';
  return <Paper
    data-result-tone={tone}
    role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
    square={embedded}
    elevation={0}
    variant={embedded ? undefined : 'outlined'}
    sx={{
      height: compact ? 'auto' : '100%', minHeight: compact ? 0 : designTokens.feedback.result.minHeight, minWidth: 0,
      display: 'grid', placeItems: 'center',
      p: `${compact ? designTokens.layout.surfacePaddingCompact : designTokens.layout.surfacePadding}px`,
      bgcolor: embedded ? 'transparent' : undefined,
      border: embedded ? 0 : undefined, borderRadius: embedded ? 0 : designTokens.radius.surface,
    }}
  >
    <Stack spacing={1} sx={{ alignItems: 'center', maxWidth: 480, minWidth: 0, textAlign: 'center' }}>
      <Box aria-hidden="true" sx={{
        width: compact ? designTokens.feedback.result.compactIconSize : designTokens.feedback.result.iconSize,
        height: compact ? designTokens.feedback.result.compactIconSize : designTokens.feedback.result.iconSize,
        mb: 0.5, display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.circle, bgcolor: background, color,
      }}><Icon sx={{ fontSize: compact ? designTokens.feedback.result.compactGlyphSize : designTokens.feedback.result.glyphSize }} /></Box>
      <Typography component="h2" variant="body1" sx={{ ...designTokens.typography.componentTitle, overflowWrap: 'anywhere' }}>{title}</Typography>
      {description != null && description !== '' ? <Typography component="div" variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{description}</Typography> : null}
      {action ? <Box sx={{ pt: 1, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, maxWidth: '100%' }}>{action}</Box> : null}
    </Stack>
  </Paper>;
}
